import { queryOptions } from "@tanstack/react-query";
import { HealthzResponseSchema } from "@nexus/contracts";
import { config } from "@/config";
import {
  HEALTH_POLL_INTERVAL,
  HEALTH_POLL_INTERVAL_DEGRADED_MS,
  HEALTH_STALE_TIME_MS,
  RETRY_COUNT_DEFAULT,
} from "@/lib/query-constants";

export type ServerConnectivity = "online" | "offline";

export interface ServerHealthPollResult {
  status: ServerConnectivity;
  liveSha?: string;
  publishedShas?: string[];
  announcementId?: string;
}

async function checkHealth(): Promise<ServerHealthPollResult> {
  const res = await fetch(`${config.apiUrl}/healthz`);
  if (!res.ok) throw new Error(`healthz returned ${res.status}`);
  const json: unknown = await res.json();
  const parsed = HealthzResponseSchema.safeParse(json);
  if (!parsed.success) throw new Error("invalid healthz response");
  return {
    status: "online",
    liveSha: parsed.data.liveSha,
    publishedShas: parsed.data.publishedShas,
    announcementId: parsed.data.announcementId,
  };
}

export const serverStatusKeys = {
  all: ["server-status"] as const,
} as const;

/** Health-check poll query. Uses `meta.skipGlobalError` — ConnectionLostBanner owns offline UX. */
export function serverStatusQueryOptions() {
  return queryOptions({
    queryKey: serverStatusKeys.all,
    queryFn: checkHealth,
    // Poll faster while failing so confirmed-offline engages sooner (3× degraded interval).
    refetchInterval: (query) =>
      query.state.status === "error"
        ? HEALTH_POLL_INTERVAL_DEGRADED_MS
        : HEALTH_POLL_INTERVAL,
    // React Query skips interval fetches while the tab is blurred, so without
    // this a backgrounded tab learns nothing until its next tick after refocus.
    // Opts out of the app-wide `refetchOnWindowFocus: false`.
    refetchOnWindowFocus: true,
    staleTime: HEALTH_STALE_TIME_MS,
    retry: RETRY_COUNT_DEFAULT,
    meta: { skipGlobalError: true },
  });
}
